import type { ChatMessageViewModel } from './chatViewModels';
import type {
  AvatarProps,
  BubbleProps,
  DayProps,
  IMessage,
  SystemMessageProps,
} from 'react-native-gifted-chat';

import { useCallback, useMemo } from 'react';

import { ActivityIndicator, Platform } from 'react-native';

import { getColorTokenValue } from '@utility-nyc/react-native-ui-config';
import { Bubble, GiftedChat } from 'react-native-gifted-chat';
import { YStack } from 'tamagui';

import { BodyS, LabelM, getTestProps } from '@south-street-app/atoms';
import { InitialsCircle } from '@south-street-app/molecules';
import { getSizeTokenValue } from '@south-street-app/theme';

import { formatChatDayDividerLabel } from './chatViewModels';
import { ChatInput } from '../ChatScreen/ChatInput';
import { ChatSendButton } from '../ChatScreen/ChatSendButton';

type GiftedChatPanelMessage = IMessage & {
  avatarLabel?: string;
  timestampLabel?: string;
};

type ChatDayDividerProps = DayProps & {
  currentMessage?: GiftedChatPanelMessage;
};

type ChatConversationPanelProps = {
  activeUserId: string;
  activeConversationUniqueName?: string;
  emptyMessage: string;
  inlineTitle?: string;
  isLoading: boolean;
  isLoadingEarlier: boolean;
  isLocked: boolean;
  isSending: boolean;
  loadEarlier: boolean;
  messages: ChatMessageViewModel[];
  onLoadEarlier: () => void;
  onSendMessage: (message: string) => void;
  placeholder: string;
  readOnly?: boolean;
  hideSenderIdentity?: boolean;
  showEmptyState?: boolean;
};

const CHAT_FONT_FAMILY = Platform.OS === 'web' ? 'Inter' : 'InterRegular';
const LOCKED_PLACEHOLDER = 'Messaging is currently unavailable.';
const MIN_COMPOSER_HEIGHT = getSizeTokenValue('$6') as number;
const MAX_COMPOSER_HEIGHT = getSizeTokenValue('$28') as number;
const SAME_SENDER_BUBBLE_SPACING = '$2';
const GROUP_BREAK_BUBBLE_SPACING = '$4';

const toGiftedChatMessage = (
  message: ChatMessageViewModel,
  activeUserId: string,
): GiftedChatPanelMessage => {
  const createdAt =
    typeof message.createdAt === 'string'
      ? new Date(message.createdAt)
      : message.createdAt ?? new Date(0);

  return {
    _id: message.id,
    text: message.text,
    createdAt,
    system: message.side === 'system',
    user: {
      _id:
        message.side === 'right'
          ? activeUserId
          : message.avatarLabel ?? 'South Street',
      name: message.avatarLabel ?? '',
    },
    ...(message.avatarLabel ? { avatarLabel: message.avatarLabel } : undefined),
    ...(message.timestampLabel
      ? { timestampLabel: message.timestampLabel }
      : undefined),
  };
};

const ResolvedSystemMessage = (
  props: SystemMessageProps<GiftedChatPanelMessage>,
) => (
  <YStack alignItems={'center'} paddingVertical={'$3'}>
    <BodyS color={'$mono500'}>{props.currentMessage.text}</BodyS>
  </YStack>
);

const GenericChatAvatar = (props: AvatarProps<GiftedChatPanelMessage>) => {
  if (props.position !== 'left') {
    return null;
  }

  const avatarName = props.currentMessage.avatarLabel ?? 'South Street';

  return (
    <YStack
      marginRight={'$2'}
      {...getTestProps(
        props.currentMessage._id
          ? `customer-chat-message-avatar-${props.currentMessage._id}`
          : undefined,
      )}
    >
      <InitialsCircle name={avatarName} size={'small'} usePadding={false} />
    </YStack>
  );
};

const isGroupedWithNextChatMessage = (
  currentMessage?: GiftedChatPanelMessage,
  nextMessage?: GiftedChatPanelMessage,
) => {
  if (!currentMessage || !nextMessage) {
    return false;
  }

  if (currentMessage.system || nextMessage.system) {
    return false;
  }

  const currentUserId = (currentMessage as Partial<GiftedChatPanelMessage>).user
    ?._id;
  const nextUserId = (nextMessage as Partial<GiftedChatPanelMessage>).user?._id;

  if (!currentUserId || !nextUserId) {
    return false;
  }

  return (
    currentUserId === nextUserId &&
    new Date(currentMessage.createdAt).toDateString() ===
      new Date(nextMessage.createdAt).toDateString()
  );
};

const GenericChatBubble = (props: BubbleProps<GiftedChatPanelMessage>) => {
  const messageId = props.currentMessage._id;
  const position = props.position;
  const marginBottom = isGroupedWithNextChatMessage(
    props.currentMessage,
    props.nextMessage,
  )
    ? SAME_SENDER_BUBBLE_SPACING
    : GROUP_BREAK_BUBBLE_SPACING;

  return (
    <YStack
      alignItems={position === 'right' ? 'flex-end' : 'flex-start'}
      marginBottom={marginBottom}
      {...getTestProps(
        messageId ? `customer-chat-message-${messageId}` : undefined,
      )}
    >
      <Bubble
        {...props}
        containerStyle={{
          left: { marginRight: 48 },
          right: { marginLeft: 48 },
        }}
        renderTime={() => null}
        textStyle={{
          left: {
            color: getColorTokenValue('$mono900'),
            fontFamily: CHAT_FONT_FAMILY,
          },
          right: {
            color: getColorTokenValue('$mono100'),
            fontFamily: CHAT_FONT_FAMILY,
          },
        }}
        wrapperStyle={{
          left: {
            backgroundColor: getColorTokenValue('$mono100'),
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingVertical: 12,
          },
          right: {
            backgroundColor: getColorTokenValue('$brand500'),
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingVertical: 12,
          },
        }}
      />
    </YStack>
  );
};

const ChatDayDivider = (props: ChatDayDividerProps) => {
  const label = formatChatDayDividerLabel(
    props.currentMessage?.createdAt ?? props.createdAt,
  );

  if (!label) {
    return null;
  }

  return (
    <YStack alignItems={'center'} paddingVertical={'$3'}>
      <BodyS color={'$mono500'}>{label}</BodyS>
    </YStack>
  );
};

const ChatConversationPanel = ({
  activeUserId,
  activeConversationUniqueName,
  emptyMessage,
  hideSenderIdentity = false,
  inlineTitle,
  isLoading,
  isLoadingEarlier,
  isLocked,
  isSending,
  loadEarlier,
  messages,
  onLoadEarlier,
  onSendMessage,
  placeholder,
  readOnly = false,
  showEmptyState = false,
}: ChatConversationPanelProps) => {
  const composerDisabled = readOnly || isLocked || isSending;
  const giftedMessages = useMemo(
    () => messages.map((message) => toGiftedChatMessage(message, activeUserId)),
    [activeUserId, messages],
  );
  const handleSend = useCallback(
    (nextMessages: GiftedChatPanelMessage[] = []) => {
      const nextMessage = nextMessages[0]?.text?.trim();

      if (!nextMessage) {
        return;
      }

      onSendMessage(nextMessage);
    },
    [onSendMessage],
  );
  const renderAvatar = hideSenderIdentity ? null : GenericChatAvatar;

  if (isLoading) {
    return (
      <YStack alignItems={'center'} flex={1} justifyContent={'center'}>
        <ActivityIndicator size={'large'} />
      </YStack>
    );
  }

  if (showEmptyState) {
    return (
      <YStack
        alignItems={'center'}
        flex={1}
        gap={'$2'}
        justifyContent={'center'}
        padding={'$6'}
      >
        {inlineTitle ? (
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        ) : null}
        <BodyS color={'$mono500'} textAlign={'center'}>
          {emptyMessage}
        </BodyS>
      </YStack>
    );
  }

  return (
    <YStack
      accessibilityLabel={'customer-active-chat-panel'}
      accessibilityValue={
        activeConversationUniqueName
          ? { text: activeConversationUniqueName }
          : undefined
      }
      data-conversation-unique-name={activeConversationUniqueName}
      flex={1}
      {...getTestProps('customer-active-chat-panel')}
    >
      {inlineTitle ? (
        <YStack
          backgroundColor={'$mono100'}
          borderBottomColor={'$mono300'}
          borderBottomWidth={1}
          paddingHorizontal={'$4'}
          paddingVertical={'$3'}
        >
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        </YStack>
      ) : null}

      <GiftedChat
        alwaysShowSend={!composerDisabled}
        isLoadingEarlier={isLoadingEarlier}
        loadEarlier={loadEarlier}
        maxComposerHeight={MAX_COMPOSER_HEIGHT}
        minComposerHeight={MIN_COMPOSER_HEIGHT}
        messages={giftedMessages}
        onLoadEarlier={onLoadEarlier}
        onSend={handleSend}
        renderAvatar={renderAvatar}
        renderAvatarOnTop={false}
        renderBubble={GenericChatBubble}
        renderDay={ChatDayDivider}
        renderInputToolbar={(props) =>
          readOnly ? null : (
            <ChatInput
              {...props}
              textInputStyle={{
                flex: 1,
                fontFamily: CHAT_FONT_FAMILY,
                fontSize: 16,
                minWidth: 0,
                ...(Platform.OS === 'web'
                  ? {
                      marginTop: 0,
                      paddingTop: 0,
                    }
                  : undefined),
              }}
            />
          )
        }
        renderLoading={() => <ActivityIndicator size={'small'} />}
        renderSend={(props) =>
          composerDisabled ? null : <ChatSendButton {...props} />
        }
        renderSystemMessage={ResolvedSystemMessage}
        renderUsername={() => null}
        showAvatarForEveryMessage={false}
        showUserAvatar={false}
        textInputProps={{
          editable: !composerDisabled,
          placeholder: isLocked ? LOCKED_PLACEHOLDER : placeholder,
        }}
        user={{
          _id: activeUserId,
          name: activeUserId,
        }}
      />
    </YStack>
  );
};

export { ChatConversationPanel, isGroupedWithNextChatMessage };
export type { ChatConversationPanelProps };
